// Same-origin API client: the frontend and API are one app on
// pmecanada.com, so every call is just /api/... with no base URL.

const TOKEN_KEY = 'pme_token';
export const UNAUTHORIZED_EVENT = 'pme:unauthorized';

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* private mode: session lasts until reload */ }
}

// code is the server's error code (e.g. 'invalid_credentials'), which the
// UI translates via errors.<code> in the i18n files.
export class ApiError extends Error {
  constructor(code, status, detail, data) {
    super(code);
    this.code = code;
    this.status = status;
    this.detail = detail;
    this.data = data || null; // full response body, for screens that show more
  }
}

export async function api(path, { method = 'GET', body, form, timeout } = {}) {
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  let payload;
  if (form) payload = form; // FormData: the browser sets the multipart boundary
  else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  // timeout (ms): a request with no answer gives up as a network error.
  const ctrl = timeout ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeout) : null;
  let res; let text;
  try {
    res = await fetch(`/api${path}`, { method, headers, body: payload, signal: ctrl?.signal });
    text = await res.text();
  } catch {
    throw new ApiError('network_error', 0);
  } finally {
    clearTimeout(timer);
  }
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = null; }

  if (!res.ok) {
    // A rejected login token (expired, suspended) ends the session
    // everywhere; a wrong password on /auth/login is just a form error.
    if (res.status === 401 && token && !path.startsWith('/auth/login')) {
      setToken(null);
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    }
    throw new ApiError(data?.error || 'server_error', res.status, data?.detail, data);
  }
  // A save went through: typed work is no longer unsaved (live.jsx).
  if (method !== 'GET' && !BACKGROUND.test(path)) window.dispatchEvent(new Event('pme:saved'));
  return data;
}
// Calls made in the background (not a user saving a form).
const BACKGROUND = /^\/(chat\/(typing|presence|conversations\/\d+\/read)|auth\/lang|workspace\/notifications|account\/settings\/seen)/;
